// ═══════════════════════════════════════════════════════════════
// Earnings — what the work made.
//
// Every number here is derived in the browser from jobs the app has
// already loaded. Nothing new is stored and nothing is sent anywhere;
// this screen is a different way of looking at data that was always
// there.
//
// The headline deliberately answers one question — "was this month
// any good?" — and the comparison line answers the follow-up without
// making the mechanic do arithmetic.
// ═══════════════════════════════════════════════════════════════
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Minus, Wallet, Wrench, Package,
  Clock, AlertCircle, Trophy,
} from 'lucide-react';
import { useUserData } from './lib/use-user-data.js';
import { formatPrice } from './lib/job-helpers.js';
import {
  PERIODS, periodRange, summarise, change, monthlySeries,
  outstandingTotal, bestMonth,
} from './lib/earnings.js';

// Whole dollars for headline figures — cents are noise at this size,
// and "$8,400" is the number someone repeats out loud.
function money(n) {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `$${Math.round(n).toLocaleString()}`;
}

function pct(n, total) {
  if (!total) return null;
  return Math.round((n / total) * 100);
}

function Delta({ value, label }) {
  if (value === null) return null;
  const rounded = Math.round(value);
  const flat = rounded === 0;
  const up = rounded > 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const cls = flat ? 'flat' : up ? 'up' : 'down';
  return (
    <span className={`earn-delta ${cls}`}>
      <Icon size={13} />
      {flat ? 'Level with' : `${up ? '+' : ''}${rounded}% vs`} {label}
    </span>
  );
}

export default function Earnings() {
  const { jobs, ready } = useUserData();
  const [period, setPeriod] = useState('month');

  // One `now` per render pass, so the period boundaries and the chart
  // can't disagree about what month it is.
  const now = useMemo(() => new Date(), []);
  const range = useMemo(() => periodRange(period, now), [period, now]);

  // Archived customers' revenue is included on purpose: the work
  // happened and was paid for. Archiving hides someone from your
  // lists; it doesn't rewrite last year's earnings.
  const current  = useMemo(() => summarise(jobs, range), [jobs, range]);
  const previous = useMemo(
    () => (range.prev ? summarise(jobs, range.prev) : null),
    [jobs, range]
  );
  const series = useMemo(() => monthlySeries(jobs, 12, now), [jobs, now]);
  const owed   = useMemo(() => outstandingTotal(jobs), [jobs]);
  const best   = useMemo(() => bestMonth(jobs), [jobs]);

  const revenueDelta = previous ? change(current.revenue, previous.revenue) : null;
  const peak = Math.max(...series.map((b) => b.revenue), 0);

  const hasAnything = current.count > 0;

  return (
    <div>
      <div className="screen-header">
        <div>
          <h1>Earnings</h1>
          <p className="subtitle">
            Counted the day the money came in, not the day you sent the invoice.
          </p>
        </div>
      </div>

      <div className="filter-row">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            className={`filter-pill ${period === p.value ? 'active' : ''}`}
            onClick={() => setPeriod(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {!ready && <p className="muted small">Working it out…</p>}

      {ready && (
        <>
          <section className="earn-headline">
            <p className="earn-period">{range.label}</p>
            <p className="earn-total">{money(current.revenue)}</p>
            <p className="earn-sub">
              {current.count} job{current.count === 1 ? '' : 's'} paid
              {current.averageTicket !== null && (
                <> · {money(current.averageTicket)} average ticket</>
              )}
            </p>
            {range.prev && revenueDelta !== null && (
              <p className="earn-deltas">
                <Delta value={revenueDelta} label={range.prev.label} />
              </p>
            )}
          </section>

          {!hasAnything && (
            <div className="empty-state">
              <Wallet size={28} color="var(--text-3)" strokeWidth={1.5} style={{ marginBottom: 8 }} />
              <p style={{ fontWeight: 600 }}>Nothing paid in {range.label}.</p>
              <p className="muted small">
                A job counts here once it's invoiced, paid, and closed.
                {owed > 0 && (
                  <> You have <Link to="/outstanding">{money(owed)} invoiced and waiting</Link>.</>
                )}
              </p>
            </div>
          )}

          {hasAnything && (
            <div className="earn-grid">
              <Breakdown label="Labor" icon={<Wrench size={12} />}
                amount={current.labor} share={pct(current.labor, current.revenue)} />
              <Breakdown label="Parts" icon={<Package size={12} />}
                amount={current.parts} share={pct(current.parts, current.revenue)} />
              <Breakdown label="Shop supplies" icon={<Package size={12} />}
                amount={current.supplies} share={pct(current.supplies, current.revenue)} />
              {current.hourly !== null && (
                <Breakdown
                  label="Per hour on site"
                  icon={<Clock size={12} />}
                  amount={current.hourly}
                  // Said out loud, because an hourly rate over half
                  // your jobs is a different number than over all of
                  // them, and a mechanic will spot the difference.
                  note={
                    current.jobsWithMinutes === current.count
                      ? `over ${current.hours.toFixed(1)}h`
                      : `over ${current.hours.toFixed(1)}h from the ${current.jobsWithMinutes} timed job${current.jobsWithMinutes === 1 ? '' : 's'}`
                  }
                />
              )}
            </div>
          )}

          {owed > 0 && (
            <Link to="/outstanding" className="earn-owed">
              <AlertCircle size={15} />
              <span>
                <strong>{money(owed)} invoiced and not yet paid.</strong>{' '}
                That isn't counted above — it lands here the day it's paid.
              </span>
            </Link>
          )}

          <section className="earn-chart-card">
            <div className="earn-chart-head">
              <h2>The last 12 months</h2>
              {best && (
                <span className="muted small">
                  <Trophy size={12} style={{ verticalAlign: '-2px', marginRight: 3 }} />
                  Best: {best.start.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                  {' · '}{money(best.revenue)}
                </span>
              )}
            </div>
            {peak === 0 ? (
              <p className="muted small empty-inline">No paid work in the last 12 months yet.</p>
            ) : (
              <ol className="earn-chart" role="list">
                {series.map((b) => (
                  <li key={b.key} className="earn-bar-cell">
                    <span className="earn-bar-value">{b.revenue > 0 ? money(b.revenue) : ''}</span>
                    <span
                      className="earn-bar"
                      style={{ height: `${peak > 0 ? Math.max((b.revenue / peak) * 100, b.revenue > 0 ? 3 : 0) : 0}%` }}
                      title={`${b.label} ${b.year}: ${money(b.revenue)} from ${b.count} job${b.count === 1 ? '' : 's'}`}
                    />
                    <span className="earn-bar-label">{b.label}</span>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <p className="muted small earn-note">
            Worked out on this device from your own jobs — nothing is sent anywhere.
            Cancelled jobs never count. Money is counted on the day it was paid, so an
            invoice sent in one month and paid in the next belongs to the month it landed.
          </p>
        </>
      )}
    </div>
  );
}

function Breakdown({ label, icon, amount, share, note }) {
  return (
    <div className="earn-cell">
      <span className="earn-cell-label">{icon} {label}</span>
      <span className="earn-cell-value">{money(amount)}</span>
      <span className="muted small">
        {note || (share !== null ? `${share}% of the total` : '')}
      </span>
    </div>
  );
}
